import { ApiError, SLOW_CALL, apiRequest } from "@common/api";
import { isAbort, RequestTimeoutError } from "@jkast90/kast-common/fetch";
import { fetchWithLogging, type FetchOptions } from "@common/fetch-logger";
import { romBaseName } from "../emulator/saves";

// Read side of the cloud library for the home page. Uploads stay in the
// player, which has the core to import into.

export interface CloudRom {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  /** The console, read by the server from the name or a zip's contents: gb, gbc, gba, n64, or one CloudBoy doesn't run (psx, psp, nds), or unknown. */
  system: string;
}

// A ROM's bytes go to and from S3 directly, up to 32 MB, with a progress
// bar: a limit that only catches a transfer that never ends.
const ROM_BYTES: FetchOptions = { timeoutMs: 10 * 60_000 };
const TIMED_OUT = "The transfer took too long. Check your connection and try again.";

// Uncached, the listing reads each zip's directory from S3 to name its console.
export async function listCloudRoms(): Promise<CloudRom[]> {
  return (await apiRequest("/library", {}, SLOW_CALL)).json();
}

// Two hops on purpose. Following the API's redirect to S3 would be simpler,
// but a cross-origin redirect of a CORS request is sent with `Origin: null`,
// which the bucket's CORS rule rejects. Fetching the presigned URL directly
// carries the page's real origin.
// `onProgress` gets 0..1 as bytes arrive, measured against the size the
// listing reported (Content-Length is not readable cross-origin).
export async function downloadCloudRom(rom: CloudRom, onProgress?: (fraction: number) => void): Promise<File> {
  const { downloadUrl } = (await (await apiRequest(`/library/${encodeURIComponent(rom.id)}/download`)).json()) as {
    downloadUrl: string;
  };
  let response: Response;
  try {
    response = await fetchWithLogging(downloadUrl, undefined, ROM_BYTES);
  } catch (error) {
    if (error instanceof RequestTimeoutError) throw new ApiError(TIMED_OUT);
    throw new ApiError("Could not reach the download. Check the bucket's CORS rule for this origin.");
  }
  if (!response.ok) throw new ApiError(`Download failed (${response.status}).`, response.status);

  const chunks: Uint8Array[] = [];
  let received = 0;
  const reader = response.body?.getReader();
  try {
    if (reader) {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        onProgress?.(rom.size ? Math.min(received / rom.size, 0.99) : 0);
      }
    } else {
      chunks.push(new Uint8Array(await response.arrayBuffer()));
    }
  } catch (error) {
    // The limit covers the body too, so a stalled download ends here.
    throw isAbort(error) ? new ApiError(TIMED_OUT) : error;
  }
  onProgress?.(1);
  return new File(chunks as BlobPart[], rom.name, { type: "application/octet-stream" });
}

const MAX_ROM_BYTES = 32 * 1024 * 1024;

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

export type UploadResult = "uploaded" | "already-there" | "too-large";

// Adds a ROM to the shared library unless it is already there (same file
// name or same contents), so games that came from the cloud are not sent
// back up. Bytes go straight to S3 through a presigned URL.
export async function uploadToLibrary(file: File, system?: string): Promise<UploadResult> {
  if (file.size > MAX_ROM_BYTES) return "too-large";
  const sha256 = await sha256Hex(file);
  const existing = await listCloudRoms();
  // Same contents, or the same game under another extension (a copy picked
  // from the computer is named after its library entry).
  if (existing.some((rom) => rom.id === sha256 || romBaseName(rom.name) === romBaseName(file.name)))
    return "already-there";
  const { uploadUrl } = (await (
    await apiRequest("/library", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: file.name, size: file.size, sha256, system }),
    })
  ).json()) as { uploadUrl: string };
  let put: Response;
  try {
    put = await fetchWithLogging(
      uploadUrl,
      {
        method: "PUT",
        // The console is signed into the URL and must come back as this header.
        headers: { "Content-Type": "application/octet-stream", ...(system && { "x-amz-meta-system": system }) },
        body: file,
      },
      ROM_BYTES,
    );
  } catch (error) {
    if (error instanceof RequestTimeoutError) throw new ApiError(TIMED_OUT);
    throw new ApiError("Could not reach the upload. Check the bucket's CORS rule for this origin.");
  }
  if (!put.ok) throw new ApiError(`Upload failed (${put.status}).`, put.status);
  // The API reads the name from a JSON body; without one it answers 415 and
  // a finished upload looked like a failed one.
  await apiRequest(`/library/${sha256}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: file.name }),
  });
  return "uploaded";
}
